import {Stack, TextField, Typography} from '@mui/material';
import {FC, useCallback} from 'react';

import {asNumber} from '@shared/lib/utils/type_utils';

import {useOptionalNumberQueryString} from '@shared_web/lib/use_query_string';

import {FILTER_LABEL_WIDTH} from '@website_frontend/components/filter_form';

type Props = {
  title: string;
  minLabel?: string;
  maxLabel?: string;
  minParamName: string;
  maxParamName: string;
};

export const FilterMinMax: FC<Props> = ({
  title,
  minLabel = 'Min',
  maxLabel = 'Max',
  minParamName,
  maxParamName,
}) => {
  const [minValue, setMinValue] = useOptionalNumberQueryString(minParamName);
  const [maxValue, setMaxValue] = useOptionalNumberQueryString(maxParamName);

  const handleNumberValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const paramName = e.currentTarget.dataset['paramName'];
      if (paramName === undefined) {
        return;
      }
      const {value} = e.target;
      const setter =
        paramName === minParamName
          ? setMinValue
          : paramName === maxParamName
            ? setMaxValue
            : undefined;
      if (!setter) {
        return;
      }
      if (value === '') {
        setter(undefined);
        return;
      }
      const newValue = asNumber(value);
      if (newValue === undefined) {
        return;
      }
      setter(newValue === 0 ? undefined : newValue);
    },
    [minParamName, maxParamName, setMinValue, setMaxValue]
  );

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="subtitle1" sx={{width: FILTER_LABEL_WIDTH, flexShrink: 0}}>
        {title}
      </Typography>
      <Stack direction="row" spacing={1} sx={{flex: 1}}>
        <TextField
          type="number"
          label={minLabel}
          value={minValue ?? ''}
          onChange={handleNumberValueChange}
          inputProps={{min: 0, 'data-param-name': minParamName}}
          size="small"
          variant="outlined"
          sx={{width: '50%', minWidth: 0}}
        />
        <TextField
          type="number"
          label={maxLabel}
          value={maxValue ?? ''}
          onChange={handleNumberValueChange}
          inputProps={{min: 0, 'data-param-name': maxParamName}}
          size="small"
          variant="outlined"
          sx={{width: '50%', minWidth: 0}}
        />
      </Stack>
    </Stack>
  );
};

FilterMinMax.displayName = 'FilterMinMax';
